import React, { useContext, useEffect } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { DefaultTokenContext } from '../../context/DefaultTokenContext';

import CustomText from '../../styles/CustomText';
import useWallet from '../../hooks/useWallet';
import useQwalletServer from '../../hooks/useQwalletServer';
import useSecureStorage from '../../hooks/useSecureStorage';

import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { BounceLoader } from "react-spinners";
import { getTelegramId } from '../../utils/telegramClientData';

const WalletRecoveryLoading: React.FC = () => {
	const { theme } = useContext(ThemeContext)
	let navigate = useNavigate();
	const { t } = useTranslation();
	let dispatch = useDispatch();

	const { restoreWallet } = useWallet()
	const { 
		storeEncryptedData, 
		storeNetworkEncryptedData,
		setLocalMnemonicHash, 
		setBlockChain, 
		setNetworkCurrency,
		setWalletAddress,
		setNetworkWalletAddress,
		overwriteWalletData,
		logOut
	} = useSecureStorage()
	const { updateAirdropWalletAddress } = useQwalletServer()

	const { getNetworkInfo} = useContext(DefaultTokenContext);
	
	const pincode_ = useSelector((state: RootState) => state.pincode.value);
	const mnemonic_ = useSelector((state: RootState) => state.mnemonic.value);

	useEffect(() => {
		const generateWallet = async () => {
			try {
				const storedMnemonic = mnemonic_;

				if (storedMnemonic) {
					// 지갑복구를 위한 니모닉코드
					const mnemonicArray = JSON.parse(storedMnemonic)
					const mnemonic = Array.isArray(mnemonicArray) ? mnemonicArray.join(' ') : mnemonicArray

					if (mnemonic.split(' ').length % 3 === 0) {

						const createdPin = pincode_;

						// 네트워크 설정 정보
						const defaultNetworks = await getNetworkInfo('')
						console.log(`defaultNetworks : `, defaultNetworks)
						const activeNetworks = defaultNetworks.filter(network => network.is_active);
						console.log(`활성화된 네트워크 목록:`, activeNetworks);

						// 활성화된 네트워크 별로 지갑 생성 및 저장
						let walletResults: any[] = [];
						for (const network of activeNetworks) {
							const result = await saveNetworkWallet(network.symbol, mnemonic, createdPin);
							if (result.result === false || result.address == null) {
								console.error(`❌ ${network.symbol} 지갑 복구 실패`);
								logOut();
								alert(`Error restoring ${network.symbol} wallet. Please restart.`);
								navigate(`/`, { replace: true });
								return;
							}
							
							// 네트워크 지갑 주소 저장
							await setNetworkWalletAddress(result.address, network.symbol);
							walletResults.push({ network: network.symbol, address: result.address });
						}
						
						// 기본 네트워크 선택 및 저장
						const primaryNetwork = activeNetworks.find(net => net.symbol === process.env.REACT_APP_NETWORK_NAME);
						if (!primaryNetwork) {
							console.error('❌ 기본 네트워크 설정 실패');
							logOut();
							alert('Error setting default network wallet. Please restart.');
							navigate(`/CreateWallet`, { replace: true });
							return;
						}

						// 로컬 스토리지에 기본 네트워크 설정
						await overwriteWalletData(primaryNetwork.symbol);

									// 로컬 스토리지에 기본 지갑주소 설정
						await setWalletAddress(walletResults.find(w => w.network === primaryNetwork.symbol)?.address);

						// 현재 기본 블록체인 저장
						await setBlockChain(primaryNetwork.symbol);

						// 현재 기본 네트워크의 메인 currency 저장
						await setNetworkCurrency(primaryNetwork.currency);

									// 로컬 니모닉 해시 저장
						await setLocalMnemonicHash(mnemonic);

						// 게임 airdrop 주소 업데이트
						const airdropNetworkName = process.env.REACT_APP_AIRDROP_ADDRESS_NETWORK_NAME?.toLowerCase() || '';
						console.log(`WalletRecoveryLoading airdropNetworkName : `, airdropNetworkName)

						const telegramId = await getTelegramId()
						const bscWallet = walletResults.find(w => w.network === airdropNetworkName)?.address || '';
						console.log(`WalletRecoveryLoading airdropNetworkName walletAddress : `, bscWallet)
						
						if (bscWallet && (await updateAirdropWalletAddress(bscWallet, telegramId))) {
							setTimeout(() => {
								navigate(`/WalletRecoveryComplete`, { replace: true, state: { } });
							}, 2000)
						} else {
							alert('Error update airdrop wallet address')
							// 첫 화면으로 이동
							setTimeout(() => {
								navigate(`/CreateWallet`, { replace: true, state: { } });
							}, 2000)
						}

						console.log('일반 지갑복구 완료')
					} else {
						throw new Error('Invalid mnemonic length')
					}
				}
			} catch (error) {
				console.error('Error restoring wallet:', error)
			}
		}

		generateWallet()
	}, [])

	/**
	 * 네트워크 지갑 생성 및 저장
	 * @param network 
	 * @param mnemonic 
	 * @param pinCode 
	 */

	const saveNetworkWallet = async (network: string, mnemonic: string, pinCode: string) => {
		try {
			const { privateKey, publicKey, address } = await restoreWallet(mnemonic, network)

			if (network == null || address == null|| null == privateKey || null == publicKey || null == mnemonic) {
				return {privateKey:null, publicKey:null, address:null, mnemonic:null, result:false}
			}

			const result = await storeNetworkEncryptedData(network, privateKey, publicKey, mnemonic, pinCode);
			if(false == result) {
				return {privateKey:null, publicKey:null, address:null, mnemonic:null, result:false}
			}

			return {privateKey, publicKey, address, mnemonic, result:true} 
		} catch (error) {
			console.error('Error save network wallet:', error)
		}
		return {privateKey:null, publicKey:null, address:null, mnemonic:null, result:false} 
	}

	const styles = {
		container: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			marginTop: "150px"
		} as React.CSSProperties,
		title: {
			fontSize: 24,
			color: theme.label_strong,
			textAlign: 'center',
			margin: "5px 0 0"
		} as React.CSSProperties,
		subTitle1: {
			fontSize: 18,
			color: theme.label_strong,
			textAlign: 'center',
			margin: "5px 0 0"
		} as React.CSSProperties,
		subTitle2: {
			fontSize: 18,
			color: theme.label_strong,
			textAlign: 'center',
			margin: "5px 0 0"
		} as React.CSSProperties
	}
	
	return (
		<div className='container' style={{ backgroundColor: theme.background_normal }}>
			<div style={styles.container}>
				<div style={{ marginBottom: "10px" }}>
					<BounceLoader color={"blue"} size={100} />
				</div>

				<CustomText weight="medium" style={{ ...styles.title}}>
					{t('wallet_recovery_complete_message_01')}
				</CustomText>

				<CustomText style={{ ...styles.subTitle1 }}>
					{t('wallet_recovery_complete_message_02')}
				</CustomText>

				<CustomText style={{ ...styles.subTitle2}}>
					{t('wallet_recovery_complete_message_03')}
				</CustomText>
			</div>
		</div>
	)
}

export default WalletRecoveryLoading
